import axios from '../config/axios';

const WEBPAGE_IMAGES_API = '/webpage-images';

export const webPageImagesService = {
  // Get all sections with their images
  getAllSections: async () => {
    try {
      const response = await axios.get(`${WEBPAGE_IMAGES_API}/sections`);
      return response.data;
    } catch (error) {
      console.error('Error fetching sections:', error);
      throw error;
    }
  },

  // Get images for a specific section
  getSectionImages: async (sectionId) => {
    try {
      const response = await axios.get(`${WEBPAGE_IMAGES_API}/sections/${sectionId}/images`);
      return response.data;
    } catch (error) {
      console.error('Error fetching section images:', error);
      throw error;
    }
  },

  // Add a new image to a section
  addImage: async (sectionId, imageData) => {
    try {
      const formData = new FormData();
      formData.append('name', imageData.name);
      formData.append('alt', imageData.alt);
      formData.append('description', imageData.description);
      
      if (imageData.file) {
        formData.append('image', imageData.file);
      } else if (imageData.url) {
        formData.append('url', imageData.url);
      }

      const response = await axios.post(`${WEBPAGE_IMAGES_API}/sections/${sectionId}/images`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error adding image:', error);
      throw error;
    }
  },

  // Update an existing image
  updateImage: async (imageId, imageData) => {
    try {
      const response = await axios.put(`${WEBPAGE_IMAGES_API}/${imageId}`, imageData);
      return response.data;
    } catch (error) {
      console.error('Error updating image:', error);
      throw error;
    }
  },

  // Delete an image
  deleteImage: async (imageId) => {
    try {
      const response = await axios.delete(`${WEBPAGE_IMAGES_API}/${imageId}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting image:', error);
      throw error;
    }
  },

  // Upload image file
  uploadImage: async (formData, onProgress) => {
    try {
      const response = await axios.post(`${WEBPAGE_IMAGES_API}/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        timeout: 60000, // 60 second timeout
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log('Upload progress:', percentCompleted + '%');
          if (onProgress) {
            onProgress(percentCompleted);
          }
        },
      });
      return response.data;
    } catch (error) {
      console.error('Error uploading image:', error);
      
      // Enhanced error handling
      if (error.response) {
        // Server responded with error status
        throw {
          ...error,
          message: error.response.data?.message || 'Upload failed',
          status: error.response.status
        };
      } else if (error.request) {
        // Network error or timeout
        throw {
          ...error,
          message: 'Network error. Please check your connection.',
          code: 'NETWORK_ERROR'
        };
      } else {
        // Other error
        throw {
          ...error,
          message: 'Upload failed. Please try again.'
        };
      }
    }
  },

  // Get image by ID
  getImageById: async (imageId) => {
    try {
      const response = await axios.get(`${WEBPAGE_IMAGES_API}/${imageId}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching image:', error);
      throw error;
    }
  },

  // Get images by category
  getImagesByCategory: async (category) => {
    try {
      const response = await axios.get(`${WEBPAGE_IMAGES_API}/category/${category}`);
      return response.data;
    } catch (error) {
      console.error('Error fetching images by category:', error);
      throw error;
    }
  },

  // Bulk update images
  bulkUpdateImages: async (updates) => {
    try {
      const response = await axios.post(`${WEBPAGE_IMAGES_API}/bulk-update`, updates);
      return response.data;
    } catch (error) {
      console.error('Error bulk updating images:', error);
      throw error;
    }
  },

  // Get image statistics
  getImageStats: async () => {
    try {
      const response = await axios.get(`${WEBPAGE_IMAGES_API}/stats`);
      return response.data;
    } catch (error) {
      console.error('Error fetching image stats:', error);
      throw error;
    }
  }
};

export default webPageImagesService;
